import { useState, useEffect, useRef } from "react";
import React from "react";
import Icon from "./Icon";
import { usePhotos } from "../usePhotos";

const PHOTO_CATEGORIES = {
  "site-visit": "Visite de site",
  "progress":   "Progression",
  "completion": "Complétion",
  "issue":      "Problème / Issue",
};

function PhotoGallery({ entityType, entityId, role, uploadedBy, allowedCategories, readOnlyCategories = [] }) {
  const [photos, upload, deletePhotoDoc, uploading] = usePhotos(entityType, entityId);
  const [lightboxGroup, setLightboxGroup] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [uploadState, setUploadState] = useState(null); // { category, files, notes, date, error }
  const fileInputRef = useRef(null);

  const canEdit = role === "admin" || role === "foreman";
  const canUpload = canEdit || role === "employee";
  const today = new Date().toISOString().slice(0, 10);

  const grouped = {};
  photos.forEach(p => {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category].push(p);
  });

  // Lightbox keyboard nav
  useEffect(() => {
    if (!lightboxGroup) return;
    const handler = (e) => {
      if (e.key === "Escape") setLightboxGroup(null);
      if (e.key === "ArrowRight") setLightboxIdx(i => Math.min(i + 1, lightboxGroup.length - 1));
      if (e.key === "ArrowLeft")  setLightboxIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxGroup]);

  const openLightbox = (group, idx) => { setLightboxGroup(group); setLightboxIdx(idx); };

  const startUpload = (category) => {
    setUploadState({ category, files: [], notes: "", date: today, error: "" });
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const oversized = files.find(f => f.size > 10 * 1024 * 1024);
    setUploadState(s => ({ ...s, files, error: oversized ? "Fichier trop volumineux (max 10 MB)" : "" }));
    e.target.value = "";
  };

  const handleConfirmUpload = async () => {
    if (!uploadState?.files?.length || uploading) return;
    const valid = uploadState.files.filter(f => f.size <= 10 * 1024 * 1024);
    for (const file of valid) {
      await upload(file, uploadState.category, uploadState.notes, uploadedBy, uploadState.date);
    }
    setUploadState(null);
  };

  const allCategories = [...allowedCategories, ...readOnlyCategories].filter((c, i, a) => a.indexOf(c) === i);
  const lightboxPhoto = lightboxGroup ? lightboxGroup[lightboxIdx] : null;

  return (
    <div style={{ padding: "4px 0" }}>
      <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleFileChange}/>

      {/* Inline upload form */}
      {uploadState && (
        <div style={{ padding: "12px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            + Photos — {PHOTO_CATEGORIES[uploadState.category] || uploadState.category}
          </div>
          {uploadState.error && (
            <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 8 }}>{uploadState.error}</div>
          )}
          {uploadState.files.length === 0 && !uploadState.error ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setUploadState(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={() => fileInputRef.current?.click()}>Choisir fichiers</button>
            </div>
          ) : uploadState.files.length > 0 && !uploadState.error ? (
            <>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8 }}>
                {uploadState.files.length} fichier(s) sélectionné(s)
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input className="form-input" placeholder="Notes (optionnel)" style={{ flex: 2, fontSize: 12 }}
                  value={uploadState.notes} onChange={e => setUploadState(s => ({ ...s, notes: e.target.value }))}/>
                <input className="form-input" type="date" style={{ flex: 1, fontSize: 12 }}
                  value={uploadState.date} onChange={e => setUploadState(s => ({ ...s, date: e.target.value }))}/>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setUploadState(null)}>Annuler</button>
                <button className="btn btn-primary btn-sm" disabled={uploading} onClick={handleConfirmUpload}>
                  {uploading ? "Téléchargement…" : "Confirmer upload"}
                </button>
              </div>
            </>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setUploadState(null)}>Annuler</button>
            </div>
          )}
        </div>
      )}

      {/* Gallery grouped by category */}
      {allCategories.map(category => {
        const isReadOnly = readOnlyCategories.includes(category);
        const catPhotos = grouped[category] || [];
        if (catPhotos.length === 0 && (isReadOnly || !canEdit)) return null;
        return (
          <div key={category} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: 1 }}>
                {isReadOnly ? "📷 " : ""}{PHOTO_CATEGORIES[category] || category}
                {catPhotos.length > 0 && <span style={{ fontWeight: 400, marginLeft: 6 }}>({catPhotos.length})</span>}
              </div>
              {!isReadOnly && canUpload && !uploadState && (
                <button className="btn btn-secondary btn-sm" onClick={() => startUpload(category)}>
                  <Icon name="plus" size={11}/> Ajouter
                </button>
              )}
            </div>
            {catPhotos.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-3)", padding: "6px 0" }}>Aucune photo</div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {catPhotos.map((photo, idx) => (
                  <div key={photo.id} style={{ position: "relative" }}>
                    <img
                      src={photo.downloadURL}
                      alt={photo.notes || photo.filename}
                      onClick={() => openLightbox(catPhotos, idx)}
                      style={{ width: 120, height: 90, objectFit: "cover", borderRadius: "var(--radius-sm)", cursor: "pointer", display: "block" }}
                    />
                    <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 3, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {photo.takenAt} · {photo.uploadedBy}
                    </div>
                    {!isReadOnly && canEdit && (
                      <button
                        className="btn-icon danger"
                        onClick={() => deletePhotoDoc(photo)}
                        style={{ position: "absolute", top: 3, right: 3, padding: 3, background: "rgba(0,0,0,0.55)", borderRadius: "var(--radius-sm)", color: "#fff" }}
                      >
                        <Icon name="trash" size={11}/>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setLightboxGroup(null); }}
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.93)", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <button onClick={() => setLightboxGroup(null)} style={{ position: "absolute", top: 16, right: 20, background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 26, lineHeight: 1 }}>✕</button>
          {lightboxIdx > 0 && (
            <button onClick={() => setLightboxIdx(i => i - 1)} style={{ position: "absolute", left: 16, background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", cursor: "pointer", padding: "12px 18px", borderRadius: "var(--radius)", fontSize: 22 }}>‹</button>
          )}
          {lightboxIdx < lightboxGroup.length - 1 && (
            <button onClick={() => setLightboxIdx(i => i + 1)} style={{ position: "absolute", right: 16, background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", cursor: "pointer", padding: "12px 18px", borderRadius: "var(--radius)", fontSize: 22 }}>›</button>
          )}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, maxWidth: "90vw" }}>
            <img
              src={lightboxPhoto.downloadURL}
              alt={lightboxPhoto.notes || lightboxPhoto.filename}
              style={{ maxWidth: "90vw", maxHeight: "78vh", objectFit: "contain", borderRadius: "var(--radius-sm)" }}
            />
            <div style={{ color: "#ccc", fontSize: 13, textAlign: "center" }}>
              <div style={{ fontWeight: 600 }}>{PHOTO_CATEGORIES[lightboxPhoto.category] || lightboxPhoto.category}</div>
              <div>{lightboxPhoto.takenAt} · {lightboxPhoto.uploadedBy}</div>
              {lightboxPhoto.notes && <div style={{ fontStyle: "italic", marginTop: 4 }}>{lightboxPhoto.notes}</div>}
            </div>
            <div style={{ color: "#666", fontSize: 11 }}>{lightboxIdx + 1} / {lightboxGroup.length}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotoGallery;
